import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AttachmentResponse } from "../types/inventoryAttachment";
import type { CreateInventoryItemRequest, InventoryItemResponse, UpdateInventoryItemRequest } from "../types/inventory";
import type { PaginatedList } from "../types/paginated";
import { apiClient } from "./client";

export interface ListInventoryParams {
  status?: string;
  audience?: string;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  page?: number;
  limit?: number;
  mine?: boolean;
}

export async function getInventoryItems(params?: ListInventoryParams): Promise<PaginatedList<InventoryItemResponse>> {
  const res = await apiClient.get<PaginatedList<InventoryItemResponse>>("/inventory", { params });
  return res.data;
}

export async function getInventoryItem(id: number): Promise<InventoryItemResponse> {
  const res = await apiClient.get<InventoryItemResponse>(`/inventory/${id}`);
  return res.data;
}

export async function createInventoryItem(data: CreateInventoryItemRequest): Promise<InventoryItemResponse> {
  const form = new FormData();
  form.append("name", data.name);
  form.append("description", data.description);
  form.append("audience", data.audience);

  if (data.improvementNotes) {
    form.append("improvementNotes", data.improvementNotes);
  }
  if (data.githubUrl) {
    form.append("githubUrl", data.githubUrl);
  }
  if (data.deploymentLocation) {
    form.append("deploymentLocation", data.deploymentLocation);
  }

  for (const participantId of data.participantIds) {
    form.append("participantIds", participantId);
  }

  for (const attachment of data.attachments ?? []) {
    form.append("attachments", attachment);
  }

  const res = await apiClient.post<InventoryItemResponse>("/inventory", form);
  return res.data;
}

export async function updateInventoryItem(
  id: number,
  data: UpdateInventoryItemRequest,
): Promise<InventoryItemResponse> {
  const res = await apiClient.patch<InventoryItemResponse>(`/inventory/${id}`, data);
  return res.data;
}

export async function deleteInventoryItem(id: number): Promise<void> {
  await apiClient.delete(`/inventory/${id}`);
}

export async function getAttachments(itemId: number): Promise<AttachmentResponse[]> {
  const res = await apiClient.get<AttachmentResponse[]>(`/inventory/${itemId}/attachments`);
  return res.data;
}

export async function uploadAttachment(itemId: number, file: File): Promise<AttachmentResponse> {
  const form = new FormData();
  form.append("file", file);
  const res = await apiClient.post<AttachmentResponse>(`/inventory/${itemId}/attachments`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function deleteAttachment(itemId: number, attachmentId: string): Promise<void> {
  await apiClient.delete(`/inventory/${itemId}/attachments/${attachmentId}`);
}

export function useInventoryItems(params?: ListInventoryParams) {
  return useQuery<PaginatedList<InventoryItemResponse>>({
    queryKey: ["inventory", params],
    queryFn: () => getInventoryItems(params),
  });
}

export function useInventoryItem(id: number) {
  return useQuery({
    queryKey: ["inventory", id],
    queryFn: () => getInventoryItem(id),
    enabled: !!id,
  });
}

export function useCreateInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createInventoryItem,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}

export function useUpdateInventoryItem(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateInventoryItemRequest) => updateInventoryItem(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inventory"] });
      void qc.invalidateQueries({ queryKey: ["inventory", id] });
    },
  });
}

export function useAttachments(itemId: number) {
  return useQuery({
    queryKey: ["attachments", itemId],
    queryFn: () => getAttachments(itemId),
    enabled: !!itemId,
  });
}

export function useUploadAttachment(itemId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadAttachment(itemId, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["attachments", itemId] });
    },
  });
}

export function useDeleteAttachment(itemId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => deleteAttachment(itemId, attachmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["attachments", itemId] });
    },
  });
}
